import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext, type AuthContextValue } from './authContext';
import toast from 'react-hot-toast';
import { api, ApiError } from '../lib/api';
import { releasePushOwnership } from '../lib/push';
import type { AuthUser } from '../lib/types';





export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [streakDays, setStreakDays] = useState(0);
  const [generalUnread, setGeneralUnread] = useState(0);
  const [pushConfigured, setPushConfigured] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.authMe();
      // Skôr než sa nastaví `user`: dopyty rámu appky sa spúšťajú práve na
      // zmenu `user` a už vtedy musia vedieť, že je to hosť.
      api.setGuestMode(data.user.role === 'guest');
      setUser(data.user);
      setCsrfToken(data.csrfToken);
      setStreakDays(data.streakDays);
      setGeneralUnread(data.generalUnread);
      setPushConfigured(data.pushConfigured);
      setMustChangePassword(Boolean(data.user.mustChangePassword));
      localStorage.setItem('whoworks:lastUserName', data.user.name);
      api.setCsrfToken(data.csrfToken);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
        setCsrfToken(null);
        setStreakDays(0);
        setMustChangePassword(false);
        api.setCsrfToken(null);
        api.setGuestMode(false);
      } else {
        toast.error((error as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      const result = await api.authLogin(username, password);
      api.setGuestMode(result.user.role === 'guest');
      setUser(result.user);
      setCsrfToken(result.csrfToken);
      setMustChangePassword(Boolean(result.mustChangePassword));
      localStorage.setItem('whoworks:lastUserName', result.user.name);
      api.setCsrfToken(result.csrfToken);
      // Pull fresh server state (streak, push config, authoritative flags).
      await refresh();
    },
    [refresh],
  );

  const changePassword = useCallback(
    async (newPassword: string, currentPassword?: string) => {
      await api.authChangePassword(newPassword, currentPassword);
      setMustChangePassword(false);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    // Best-effort: if this device's push subscription belongs to the user who
    // is logging out, unsubscribe it and forget the stored ownership so the
    // next login can't inherit (or self-heal) it. Must never block logout.
    if (user) {
      try {
        await releasePushOwnership(user.id);
      } catch {
        // Ignore — logging out matters more than push cleanup.
      }
    }
    try {
      await api.authLogout();
    } finally {
      setUser(null);
      setCsrfToken(null);
      setStreakDays(0);
      setMustChangePassword(false);
      api.setCsrfToken(null);
      api.setGuestMode(false);
    }
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      csrfToken,
      streakDays,
      generalUnread,
      pushConfigured,
      mustChangePassword,
      login,
      changePassword,
      refresh,
      logout,
    }),
    [
      user,
      loading,
      csrfToken,
      streakDays,
      generalUnread,
      pushConfigured,
      mustChangePassword,
      login,
      changePassword,
      refresh,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
